import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { api } from '../../api/client';
import { auth, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { queryKeys, useAccounts, useEntitlement, useProfile, useSubmitSupportRequest, useSupportRequests } from '../../hooks/useApi';
import { trackEvent } from '../../services/analytics';
import {
  ExternalAccount,
  SupportRequest,
  SupportRequestAttachment,
  SupportRequestCategory,
  SupportRequestKind,
  SupportRequestPayload,
} from '../../types';
import { Badge, type BadgeTone } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { PageShell } from '../components/PageShell';
import { Panel } from '../components/Panel';
import { SegmentedControl } from '../components/SegmentedControl';
import { SkeletonList } from '../components/Skeleton';
import { cx, FOCUS_RING, FOCUS_RING_SURFACE } from '../components/styles';
import { useSeatSession } from '../useSeatSession';
import { canSeeBuyingTools } from '../useV2Nav';
import { TakeTheTourButton } from '../tour/TakeTheTourButton';

/**
 * Support / bug request form.
 *
 * The form changes shape with the category, because a report that says "my
 * tickets are wrong" without naming the connection or the event is a
 * follow-up email waiting to happen. Connection and event pickers are fed
 * from the account's own data so the user picks rather than types; a
 * "not listed" escape hatch keeps the form usable when the thing they are
 * reporting is precisely that something is missing.
 *
 * Not gated on a subscription (see V2_BILLING_BYPASS_PATHS) and visible to
 * seat members, whose reports are filed against the owner's account by the
 * backend.
 */

const SUPPORT_EMAIL = 'support@flashtickets.fm';

const inputClass = cx(
  'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white',
  'placeholder-white/30 transition-colors focus:border-white/20 focus:outline-none',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  FOCUS_RING_SURFACE,
);

const selectClass = cx(inputClass, 'appearance-none pr-10 [&>option]:bg-[#141414]');

const labelClass = 'mb-2 block text-xs font-medium uppercase tracking-wider text-white/40';

const hintClass = 'mt-1.5 text-xs text-white/35';

interface CategoryDef {
  value: SupportRequestCategory;
  label: string;
  /** Which extra fields the category needs. */
  needsConnection: boolean;
  needsEvent: boolean;
  /** Restrict the connection picker to one platform (Flash Buy is DICE-only). */
  platform?: ExternalAccount['platform'];
  connectionLabel: string;
  descriptionPlaceholder: string;
}

const CATEGORIES: CategoryDef[] = [
  {
    value: 'connection',
    label: 'A connected account (DICE / TAO / AXS)',
    needsConnection: true,
    needsEvent: false,
    connectionLabel: 'Which connection?',
    descriptionPlaceholder:
      "What's happening with this account? e.g. it keeps asking to reconnect, sync fails, cards are missing, it shows as banned.",
  },
  {
    value: 'tickets_events',
    label: 'Tickets or events',
    needsConnection: true,
    needsEvent: true,
    connectionLabel: 'Which connection holds the tickets?',
    descriptionPlaceholder:
      "What's wrong with the tickets? e.g. tickets missing after sync, wrong quantity, event shows the wrong date, tickets marked past too early.",
  },
  {
    value: 'flash_buy',
    label: 'Flash Buy',
    needsConnection: true,
    needsEvent: true,
    platform: 'DICE',
    connectionLabel: 'Example DICE account the drop ran on',
    descriptionPlaceholder:
      'What happened during the drop? e.g. bought the wrong tier, did not fire, purchased on some accounts but not others. Include the drop time if you know it.',
  },
  {
    value: 'transfers',
    label: 'Transfers',
    needsConnection: true,
    needsEvent: true,
    connectionLabel: 'Which connection sent the transfer?',
    descriptionPlaceholder:
      'What went wrong with the transfer? Include the recipient email and what the transfer log shows if you can.',
  },
  {
    value: 'waitlist',
    label: 'Waitlists',
    needsConnection: true,
    needsEvent: true,
    connectionLabel: 'Which DICE account?',
    descriptionPlaceholder:
      'What happened with the waitlist? e.g. bulk join stuck, joined but not showing, wrong event matched.',
  },
  {
    value: 'billing',
    label: 'Billing or subscription',
    needsConnection: false,
    needsEvent: false,
    connectionLabel: '',
    descriptionPlaceholder:
      'Tell us about the charge or plan. Include the invoice number or date if you have it.',
  },
  {
    value: 'other',
    label: 'Something else',
    needsConnection: false,
    needsEvent: false,
    connectionLabel: '',
    descriptionPlaceholder: "Tell us what you're experiencing, and what you expected to happen instead.",
  },
];

const NOT_LISTED = '__other__';

// Mirrors the storage rule for support_attachments/ and the serializer cap.
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'done' | 'error';
  /** Download URL once uploaded. */
  url?: string;
  error?: string;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Upload one screenshot straight from the browser to Firebase Storage and
 * return its tokened download URL. The path carries the uploader's uid, which
 * is what the storage rule checks; the backend then only accepts URLs that
 * point under that prefix.
 */
async function uploadAttachment(file: File): Promise<string> {
  const user = auth.currentUser;
  if (!user || !storage) throw new Error('Screenshots need a signed-in Firebase session.');
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(0, 80) || 'screenshot';
  const path = `support_attachments/${user.uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
  const target = storageRef(storage, path);
  await uploadBytes(target, file, {
    contentType: file.type,
    customMetadata: { userId: user.uid },
  });
  return getDownloadURL(target);
}

const STATUS_TONE: Record<SupportRequest['status'], BadgeTone> = {
  open: 'blue',
  in_progress: 'amber',
  resolved: 'green',
  closed: 'neutral',
};

function accountLabel(a: ExternalAccount): string {
  const parts = [a.platform, a.phone || a.phone_masked || '', a.email || ''].filter(Boolean);
  return parts.join(' · ');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Distinct event names across the account's tickets, past ones included —
 *  the event someone is writing about is often the one that just happened. */
function useEventNames(enabled: boolean): string[] {
  const { data } = useQuery({
    queryKey: [...queryKeys.tickets, { include_past: true, for: 'support' }],
    queryFn: () => api.getTickets({ include_past: true }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const names = new Set<string>();
    (data?.tickets ?? []).forEach((t) => {
      const eventInfo = typeof t.event === 'object' ? (t.event as any) : null;
      const name = eventInfo?.name || t.event_name;
      if (name) names.add(String(name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [data]);
}

export const SupportScreen: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { isSeat, ownerEmail } = useSeatSession();
  const { data: accounts, isLoading: accountsLoading } = useAccounts();
  const { data: previous, isLoading: previousLoading, error: previousError } = useSupportRequests();
  const submit = useSubmitSupportRequest();

  useEffect(() => { trackEvent('support_page_viewed'); }, []);

  const signedInEmail = profile?.email || user?.email || '';
  const accountEmail = isSeat && ownerEmail ? ownerEmail : signedInEmail;

  const [kind, setKind] = useState<SupportRequestKind>('support');
  const [category, setCategory] = useState<SupportRequestCategory>('connection');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTouched, setContactTouched] = useState(false);
  const [connectionChoice, setConnectionChoice] = useState<string>('');
  const [connectionLabel, setConnectionLabel] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventUrl, setEventUrl] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [submitted, setSubmitted] = useState<SupportRequest | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Admin impersonation has no Firebase user, and a build without a bucket has
  // no storage: either way the upload UI is replaced by a one-line reason.
  const canAttach = Boolean(storage) && Boolean(user);
  const attachUnavailableReason = !storage
    ? 'Screenshot uploads are not configured in this build.'
    : !user
      ? 'Screenshots cannot be attached from an admin session.'
      : '';

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const gone = prev.find((a) => a.id === id);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  // Preview URLs are object URLs; release them when the screen unmounts.
  useEffect(() => () => { attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;

    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      showError(`You can attach up to ${MAX_ATTACHMENTS} screenshots.`);
      return;
    }
    const accepted: PendingAttachment[] = [];
    for (const file of incoming.slice(0, room)) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        showError(`${file.name}: only JPG, PNG, GIF or WebP images can be attached.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        showError(`${file.name} is ${formatBytes(file.size)} — the limit is 10 MB.`);
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'uploading',
      });
    }
    if (incoming.length > room) {
      showError(`Only the first ${room} of those were added — ${MAX_ATTACHMENTS} screenshots max.`);
    }
    if (accepted.length === 0) return;

    setAttachments((prev) => [...prev, ...accepted]);
    accepted.forEach((att) => {
      uploadAttachment(att.file)
        .then((url) => {
          setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, status: 'done', url } : a)));
        })
        .catch((err: any) => {
          const message = err?.message || 'Upload failed';
          setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, status: 'error', error: message } : a)));
        });
    });
  }, [attachments.length, showError]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!canAttach) return;
    addFiles(e.dataTransfer.files);
  };

  // Prefill once the profile arrives, but never overwrite what the user typed.
  useEffect(() => {
    if (!contactTouched && signedInEmail && !contactEmail) setContactEmail(signedInEmail);
  }, [signedInEmail, contactEmail, contactTouched]);

  // Flash Buy is invite-only, so an account that cannot see the Drops tab must
  // not see it offered as a category here either — the same rule as the nav.
  const { data: entitlement } = useEntitlement();
  const showFlashBuy = canSeeBuyingTools(entitlement);
  const categories = useMemo(
    () => CATEGORIES.filter((c) => c.value !== 'flash_buy' || showFlashBuy),
    [showFlashBuy],
  );

  const def = CATEGORIES.find((c) => c.value === category) ?? CATEGORIES[CATEGORIES.length - 1];
  const eventNames = useEventNames(def.needsEvent);

  const pickableAccounts = useMemo(
    () => (accounts ?? []).filter((a) => !def.platform || a.platform === def.platform),
    [accounts, def.platform],
  );

  // A stale connection choice from a previous category must not leak into a
  // payload where it no longer makes sense.
  useEffect(() => {
    setConnectionChoice('');
    setConnectionLabel('');
  }, [category]);

  const descriptionOk = description.trim().length >= 10;
  const subjectOk = subject.trim().length > 0;
  const connectionOk = !def.needsConnection || connectionChoice !== '';
  const uploading = attachments.some((a) => a.status === 'uploading');
  const canSubmit = descriptionOk && subjectOk && connectionOk && !uploading && !submit.isPending;

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setSteps('');
    setOccurredAt('');
    setEventName('');
    setEventUrl('');
    setConnectionChoice('');
    setConnectionLabel('');
    setAttachments((prev) => {
      prev.forEach((a) => URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload: SupportRequestPayload = {
      kind,
      category,
      subject: subject.trim(),
      description: description.trim(),
      steps_to_reproduce: kind === 'bug' ? steps.trim() : '',
      contact_email: contactEmail.trim() || undefined,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };
    if (def.needsConnection) {
      if (connectionChoice === NOT_LISTED) {
        payload.connection_id = null;
        payload.connection_label = connectionLabel.trim();
      } else if (connectionChoice) {
        payload.connection_id = Number(connectionChoice);
      }
    }
    if (def.needsEvent) {
      payload.event_name = eventName.trim();
      payload.event_url = eventUrl.trim();
    }
    if (occurredAt) {
      const d = new Date(occurredAt);
      if (!Number.isNaN(d.getTime())) payload.occurred_at = d.toISOString();
    }
    // Failed uploads are dropped rather than blocking the send: the words
    // matter more than the picture, and the user has already seen the error.
    const uploaded: SupportRequestAttachment[] = attachments
      .filter((a): a is PendingAttachment & { url: string } => a.status === 'done' && Boolean(a.url))
      .map((a) => ({ url: a.url, name: a.file.name, size: a.file.size, content_type: a.file.type }));
    if (uploaded.length) payload.attachments = uploaded;

    try {
      const res = await submit.mutateAsync(payload);
      trackEvent('support_request_submitted', { kind, category, attachments: uploaded.length });
      setSubmitted(res.data);
      showSuccess(res.message || `Request ${res.data.reference} sent.`);
      resetForm();
    } catch (err: any) {
      showError(err?.message || 'Could not send your request. Please try again.');
    }
  };

  const kindOptions = [
    { value: 'support' as const, label: 'Ask for help' },
    { value: 'bug' as const, label: 'Report a bug' },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Support"
        subtitle={
          <>
            Report a bug or ask for help. We reply by email, usually within a business day. You can also write to{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#60a5fa] hover:text-[#93c5fd]">
              {SUPPORT_EMAIL}
            </a>
            .
          </>
        }
        actions={<TakeTheTourButton />}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* ── Form ─────────────────────────────────────────────────────── */}
        <div className="min-w-0 space-y-6">
          {submitted && (
            <Panel className="border-emerald-500/20 bg-emerald-500/[0.04]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-300">
                    Sent — your reference is {submitted.reference}
                  </p>
                  <p className="mt-1 text-sm text-white/50">
                    We'll reply to <span className="text-white/80">{submitted.contact_email}</span>. Quote{' '}
                    {submitted.reference} if you follow up by email.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSubmitted(null)}
                  className={cx(
                    'flex-shrink-0 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/60 hover:bg-white/10 hover:text-white/80 transition-all',
                    FOCUS_RING,
                  )}
                >
                  Dismiss
                </button>
              </div>
            </Panel>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Panel>
              <div className="space-y-6">
                {/* What kind of request */}
                <div>
                  <span className={labelClass}>What do you need?</span>
                  <SegmentedControl
                    tourId="support-kind"
                    options={kindOptions}
                    value={kind}
                    onChange={setKind}
                    label="Request type"
                    stretch
                  />
                </div>

                {/* Category */}
                <div>
                  <label htmlFor="support-category" className={labelClass}>
                    What is it about?
                  </label>
                  <select
                    id="support-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as SupportRequestCategory)}
                    className={selectClass}
                  >
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Account + contact */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <span className={labelClass}>Account</span>
                    <div className="truncate rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-2.5 text-sm text-white/70">
                      {accountEmail || '—'}
                    </div>
                    <p className={hintClass}>
                      {isSeat
                        ? 'You are filing this as a team member of this account.'
                        : 'The FlashTools account this request is about.'}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="support-contact" className={labelClass}>
                      Reply to
                    </label>
                    <input
                      id="support-contact"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => { setContactTouched(true); setContactEmail(e.target.value); }}
                      placeholder="you@example.com"
                      className={inputClass}
                      autoComplete="email"
                    />
                    <p className={hintClass}>Where we should send the answer.</p>
                  </div>
                </div>

                {/* Connection picker */}
                {def.needsConnection && (
                  <div>
                    <label htmlFor="support-connection" className={labelClass}>
                      {def.connectionLabel}
                    </label>
                    <select
                      id="support-connection"
                      value={connectionChoice}
                      onChange={(e) => setConnectionChoice(e.target.value)}
                      disabled={accountsLoading}
                      className={selectClass}
                      required
                    >
                      <option value="" disabled>
                        {accountsLoading
                          ? 'Loading connections…'
                          : pickableAccounts.length
                            ? 'Choose a connection…'
                            : `No ${def.platform ?? ''} connections on this account`.replace('  ', ' ')}
                      </option>
                      {pickableAccounts.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {accountLabel(a)}
                          {a.is_banned ? ' — banned' : !a.is_active ? ' — inactive' : a.needs_reconnect || a.failed_refresh ? ' — needs reconnect' : ''}
                        </option>
                      ))}
                      <option value={NOT_LISTED}>Not listed / not sure</option>
                    </select>
                    {connectionChoice === NOT_LISTED && (
                      <input
                        type="text"
                        value={connectionLabel}
                        onChange={(e) => setConnectionLabel(e.target.value)}
                        placeholder="Describe it — e.g. the TAO account ending 4321, or the account I just disconnected"
                        className={cx(inputClass, 'mt-2')}
                        aria-label="Describe the connection"
                      />
                    )}
                    {def.value === 'flash_buy' && (
                      <p className={hintClass}>
                        One account is enough. If it happened on several, mention that in the description.
                      </p>
                    )}
                  </div>
                )}

                {/* Event */}
                {def.needsEvent && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label htmlFor="support-event" className={labelClass}>
                        Event
                      </label>
                      <input
                        id="support-event"
                        type="text"
                        list="support-event-names"
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                        placeholder="Start typing to pick from your tickets"
                        className={inputClass}
                        autoComplete="off"
                      />
                      <datalist id="support-event-names">
                        {eventNames.map((n) => (
                          <option key={n} value={n} />
                        ))}
                      </datalist>
                    </div>
                    <div className="min-w-0">
                      <label htmlFor="support-event-url" className={labelClass}>
                        Event link <span className="normal-case tracking-normal text-white/25">(optional)</span>
                      </label>
                      <input
                        id="support-event-url"
                        type="url"
                        value={eventUrl}
                        onChange={(e) => setEventUrl(e.target.value)}
                        placeholder="https://dice.fm/event/…"
                        className={inputClass}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label htmlFor="support-subject" className={labelClass}>
                    Subject
                  </label>
                  <input
                    id="support-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    placeholder={kind === 'bug' ? 'One line: what broke' : 'One line: what you need'}
                    className={inputClass}
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="support-description" className={labelClass}>
                    {kind === 'bug' ? "What's going wrong?" : 'How can we help?'}
                  </label>
                  <textarea
                    id="support-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    maxLength={10000}
                    placeholder={def.descriptionPlaceholder}
                    className={cx(inputClass, 'resize-y min-h-[120px]')}
                    required
                  />
                  {description.length > 0 && !descriptionOk && (
                    <p className="mt-1.5 text-xs text-amber-400/80">A little more detail helps us fix it first time.</p>
                  )}
                </div>

                {/* Bug-only: steps */}
                {kind === 'bug' && (
                  <div>
                    <label htmlFor="support-steps" className={labelClass}>
                      Steps to reproduce <span className="normal-case tracking-normal text-white/25">(optional)</span>
                    </label>
                    <textarea
                      id="support-steps"
                      value={steps}
                      onChange={(e) => setSteps(e.target.value)}
                      rows={3}
                      maxLength={10000}
                      placeholder={'1. Go to…\n2. Click…\n3. See…'}
                      className={cx(inputClass, 'resize-y')}
                    />
                  </div>
                )}

                {/* When */}
                <div className="sm:max-w-xs">
                  <label htmlFor="support-when" className={labelClass}>
                    When did it happen? <span className="normal-case tracking-normal text-white/25">(optional)</span>
                  </label>
                  <input
                    id="support-when"
                    type="datetime-local"
                    value={occurredAt}
                    onChange={(e) => setOccurredAt(e.target.value)}
                    className={cx(inputClass, '[color-scheme:dark]')}
                  />
                </div>

                {/* Screenshots */}
                <div>
                  <span className={labelClass}>
                    Screenshots <span className="normal-case tracking-normal text-white/25">(optional, up to {MAX_ATTACHMENTS})</span>
                  </span>
                  {canAttach ? (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES.join(',')}
                        multiple
                        className="sr-only"
                        onChange={(e) => {
                          if (e.target.files) addFiles(e.target.files);
                          e.target.value = '';
                        }}
                        aria-label="Add screenshots"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        disabled={attachments.length >= MAX_ATTACHMENTS}
                        className={cx(
                          'flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/[0.14] bg-white/[0.02] px-4 py-4 text-sm text-white/50 transition-colors',
                          'hover:border-white/25 hover:text-white/70 disabled:cursor-not-allowed disabled:opacity-40',
                          FOCUS_RING_SURFACE,
                        )}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M4 16.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5M12 4v11M7.5 8.5 12 4l4.5 4.5" />
                        </svg>
                        {attachments.length >= MAX_ATTACHMENTS
                          ? `${MAX_ATTACHMENTS} screenshots attached`
                          : 'Click to add screenshots, or drop them here'}
                      </button>
                      <p className={hintClass}>JPG, PNG, GIF or WebP, 10 MB each. A picture of the error saves a round trip.</p>
                      {attachments.length > 0 && (
                        <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                          {attachments.map((a) => (
                            <li
                              key={a.id}
                              className={cx(
                                'relative overflow-hidden rounded-xl border bg-white/[0.02]',
                                a.status === 'error' ? 'border-red-500/40' : 'border-white/[0.08]',
                              )}
                            >
                              <img
                                src={a.previewUrl}
                                alt={a.file.name}
                                className={cx('aspect-[4/3] w-full object-cover', a.status !== 'done' && 'opacity-50')}
                              />
                              <div className="px-2 py-1.5">
                                <p className="truncate text-[11px] text-white/60" title={a.file.name}>{a.file.name}</p>
                                <p className={cx('text-[10px]', a.status === 'error' ? 'text-red-400' : 'text-white/35')}>
                                  {a.status === 'uploading'
                                    ? 'Uploading…'
                                    : a.status === 'error'
                                      ? a.error || 'Upload failed'
                                      : formatBytes(a.file.size)}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAttachment(a.id)}
                                aria-label={`Remove ${a.file.name}`}
                                className={cx(
                                  'absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white/80 hover:bg-black/90 hover:text-white',
                                  FOCUS_RING,
                                )}
                              >
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                  <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <p className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-xs text-white/40">
                      {attachUnavailableReason} You can reply to our email with images instead.
                    </p>
                  )}
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-white/[0.06] pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-white/35">
                    We attach your plan, connection states and the page you were on, so you don't have to.
                  </p>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={cx(
                      'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all',
                      'bg-[#3b82f6] text-white hover:bg-[#2f6fdb]',
                      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[#3b82f6]',
                      FOCUS_RING,
                    )}
                  >
                    {submit.isPending
                      ? 'Sending…'
                      : uploading
                        ? 'Uploading screenshots…'
                        : kind === 'bug' ? 'Send bug report' : 'Send request'}
                  </button>
                </div>
              </div>
            </Panel>
          </form>
        </div>

        {/* ── Previous requests ─────────────────────────────────────────── */}
        <div className="min-w-0">
          <Panel
            title="Your requests"
            hint="Newest first. We update the status as we work through them."
            tourId="support-requests"
          >
            {previousLoading ? (
              <SkeletonList count={3} as="row" />
            ) : previousError ? (
              <EmptyState
                tone="error"
                title={previousError instanceof Error ? previousError.message : 'Could not load your requests'}
              />
            ) : !previous || previous.length === 0 ? (
              <EmptyState title="No requests yet" body="Anything you send from this page will show up here." />
            ) : (
              <ul className="space-y-2">
                {previous.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-white">{r.subject}</p>
                        <p className="mt-0.5 text-xs text-white/40">
                          {r.reference} · {r.kind_label} · {r.category_label}
                          {r.connection_label ? ` · ${r.connection_label}` : ''}
                          {r.attachments?.length
                            ? ` · ${r.attachments.length} screenshot${r.attachments.length !== 1 ? 's' : ''}`
                            : ''}
                        </p>
                      </div>
                      <Badge tone={STATUS_TONE[r.status] ?? 'neutral'} size="sm" dot>
                        {r.status_label}
                      </Badge>
                    </div>
                    <p className="mt-1.5 text-[11px] text-white/30">
                      {formatDate(r.created_at)}
                      {r.resolved_at ? ` · resolved ${formatDate(r.resolved_at)}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </PageShell>
  );
};
